import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, Users, CreditCard, Gift, DollarSign, 
  ArrowLeft, RefreshCw, Download, Target, Award
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4'];

export default function AnalyticsDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeChart, setActiveChart] = useState('revenue');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  async function fetchAnalytics() {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/admin/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAnalytics(await response.json());
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
    setIsLoading(false);
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  const { summary, time_series, distributions, top_referrers } = analytics || {};

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/admin')} variant="ghost" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5 mr-2" />Volver
            </Button>
            <div>
              <h1 className="font-manrope font-bold text-2xl">Analytics Dashboard</h1>
              <p className="text-gray-400 text-sm">Métricas y tendencias del CNI</p>
            </div>
          </div>
          <Button onClick={fetchAnalytics} variant="outline" className="border-[#2a2a2a] text-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" />Actualizar
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <KPICard icon={DollarSign} label="Ingresos Totales" value={`$${(summary?.total_revenue || 0).toLocaleString()}`} color="amber" />
          <KPICard icon={Users} label="Miembros Activos" value={summary?.total_members || 0} color="green" />
          <KPICard icon={Gift} label="Referidos" value={summary?.total_referrals || 0} color="purple" />
          <KPICard icon={Target} label="Códigos Activos" value={summary?.active_referral_codes || 0} color="blue" />
          <KPICard icon={CreditCard} label="Descuentos Dados" value={`$${(summary?.total_discounts_given || 0).toLocaleString()}`} color="pink" />
          <KPICard icon={Award} label="Recompensas" value={`$${(summary?.total_rewards_earned || 0).toLocaleString()}`} color="cyan" />
        </div>

        {/* Chart Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'revenue', label: 'Ingresos', icon: DollarSign },
            { id: 'members', label: 'Membresías', icon: Users },
            { id: 'growth', label: 'Crecimiento', icon: TrendingUp },
            { id: 'referrals', label: 'Referidos', icon: Gift }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveChart(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeChart === tab.id 
                  ? 'bg-amber-500 text-black font-semibold' 
                  : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a]'
              }`}
            >
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {/* Main Chart Area */}
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">
            {activeChart === 'revenue' && 'Ingresos Mensuales (MXN)'}
            {activeChart === 'members' && 'Nuevas Membresías por Mes'}
            {activeChart === 'growth' && 'Crecimiento de Profesionales'}
            {activeChart === 'referrals' && 'Actividad de Referidos'}
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {activeChart === 'revenue' && (
                <AreaChart data={time_series?.revenue || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px' }}
                    formatter={(value) => [`$${value.toLocaleString()} MXN`, 'Ingresos']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.3} strokeWidth={2} />
                </AreaChart>
              )}
              {activeChart === 'members' && (
                <BarChart data={time_series?.members || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px' }}
                  />
                  <Bar dataKey="nuevos" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              )}
              {activeChart === 'growth' && (
                <LineChart data={time_series?.growth || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px' }}
                  />
                  <Line type="monotone" dataKey="nuevos" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} name="Nuevos" />
                  <Line type="monotone" dataKey="total" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b' }} name="Acumulado" />
                  <Legend />
                </LineChart>
              )}
              {activeChart === 'referrals' && (
                <BarChart data={time_series?.referrals || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px' }}
                  />
                  <Bar dataKey="referidos" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Referidos" />
                  <Legend />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bottom Section: Distributions & Top Referrers */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Membership Status Pie */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">Estado de Membresías</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={distributions?.membership_status || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {(distributions?.membership_status || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px' }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* By Rank Bar */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">Distribución por Rango</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(distributions?.by_rank || []).slice(0, 5)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis type="number" stroke="#6b7280" />
                  <YAxis dataKey="name" type="category" stroke="#6b7280" width={100} tick={{ fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px' }}
                  />
                  <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Referrers */}
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" />Top Referidores
            </h3>
            {(top_referrers || []).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Gift className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aún no hay referidores</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(top_referrers || []).map((ref, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#111111] rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-amber-500 text-black' : 'bg-[#2a2a2a] text-gray-400'
                      }`}>{i + 1}</span>
                      <div>
                        <p className="text-white text-sm font-medium">{ref.name}</p>
                        <p className="text-gray-500 text-xs">{ref.referidos} referidos</p>
                      </div>
                    </div>
                    <span className="text-green-400 font-semibold text-sm">${ref.ganado.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Geographic Distribution */}
        <div className="mt-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">Distribución Geográfica (Top 10 Estados)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distributions?.by_state || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="name" stroke="#6b7280" tick={{ fontSize: 10 }} />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px' }}
                />
                <Bar dataKey="value" fill="#06b6d4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    amber: 'text-amber-400 bg-amber-500/10',
    green: 'text-green-400 bg-green-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    pink: 'text-pink-400 bg-pink-500/10',
    cyan: 'text-cyan-400 bg-cyan-500/10'
  };
  
  return (
    <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-4">
      <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-gray-400 text-xs mb-1">{label}</p>
      <p className="text-white font-bold text-lg">{value}</p>
    </div>
  );
}
